import { getFullKey } from '../LocalStorage';

export type StorageArea = 'local' | 'session';

const getStorage = (area: StorageArea): Storage => (area === 'local' ? window.localStorage : window.sessionStorage);

/**
 * sessionStorage is scoped to a single tab already, so only localStorage needs the company/user key scheme.
 */
const getStorageKey = (area: StorageArea, storageKey: string) =>
    area === 'local' ? getFullKey(storageKey) : storageKey;

/**
 * Writes are announced on the window so that every reader of the same key stays in sync, no matter which React
 * root it lives in. That's what lets components in separate roots (e.g. an Angular-mounted view selector and the
 * table it belongs to) observe each other's state without a shared context.
 */
const getEventType = (area: StorageArea, storageKey: string) =>
    `${area}-storage-update-${getStorageKey(area, storageKey)}`;

/**
 * The stored string as is, or `null` when nothing is stored. Strings compare by value, which makes this a snapshot
 * `useSyncExternalStore` can hold on to directly: it changes exactly when the stored value does.
 */
export function readRawFromStorage(area: StorageArea, storageKey: string): string | null {
    return getStorage(area).getItem(getStorageKey(area, storageKey));
}

/** Parses what {@link readRawFromStorage} returned, falling back to `init` when nothing (readable) is stored. */
export function parseStoredValue<T>(raw: string | null, init: T): T {
    if (raw === null) {
        return init;
    }
    try {
        return JSON.parse(raw) as T;
    } catch {
        return init;
    }
}

export function readFromStorage<T>(area: StorageArea, storageKey: string, init: T): T {
    return parseStoredValue(readRawFromStorage(area, storageKey), init);
}

export function writeToStorage<T>(area: StorageArea, storageKey: string, value: T): void {
    getStorage(area).setItem(getStorageKey(area, storageKey), JSON.stringify(value));
    notifyStorageUpdate(area, storageKey);
}

export function removeFromStorage(area: StorageArea, storageKey: string): void {
    getStorage(area).removeItem(getStorageKey(area, storageKey));
    notifyStorageUpdate(area, storageKey);
}

export function notifyStorageUpdate(area: StorageArea, storageKey: string): void {
    window.dispatchEvent(new CustomEvent(getEventType(area, storageKey)));
}

/**
 * Calls `onChange` whenever the stored value behind `storageKey` might have changed. The current value is not passed
 * along — subscribers read it back themselves, so that removals and writes are handled the same way.
 */
export function subscribeToStorage(area: StorageArea, storageKey: string, onChange: () => void): () => void {
    const eventType = getEventType(area, storageKey);
    window.addEventListener(eventType, onChange);

    // localStorage is shared between tabs, so writes made in another tab only show up as a native storage event.
    const handleStorageEvent = (event: StorageEvent) => {
        if (event.storageArea === getStorage(area) && event.key === getStorageKey(area, storageKey)) {
            onChange();
        }
    };

    if (area === 'local') {
        window.addEventListener('storage', handleStorageEvent);
    }

    return () => {
        window.removeEventListener(eventType, onChange);
        if (area === 'local') {
            window.removeEventListener('storage', handleStorageEvent);
        }
    };
}
